import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Card,
  Typography,
  Space,
  Button,
  Input,
  Modal,
  message,
  Switch,
  Form,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { AdminNavBar } from "../components/AdminNavBar";
import { BlogMediaEditor } from "../components/blog/BlogMediaEditor";
import { trackFeatureUsage } from "../lib/posthog";
import { useUser } from "../hooks/useUser";
import type { BlogMediaItem, BlogPost } from "../types/blogPost";
import {
  normalizeBlogMedia,
  removeBlogPostMediaFolder,
} from "../utils/blogMediaUtils";

const { Title } = Typography;
const { TextArea } = Input;

const BlogAdmin: React.FC = () => {
  const { user } = useUser();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Stable id for create flow so media can upload before insert */
  const [draftPostId, setDraftPostId] = useState<string | null>(null);
  const [coverImagePath, setCoverImagePath] = useState<string | null>(null);
  const [media, setMedia] = useState<BlogMediaItem[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm<{
    title: string;
    excerpt: string;
    body: string;
    is_published: boolean;
  }>();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      message.error("Failed to load posts: " + error.message);
      setPosts([]);
      setLoading(false);
      return;
    }

    const list = ((data as BlogPost[]) ?? []).map((p) => ({
      ...p,
      cover_image_path: p.cover_image_path ?? null,
      media: normalizeBlogMedia(p.media),
    }));
    if (list.length === 0) {
      setPosts(list);
      setLoading(false);
      return;
    }

    const { data: viewCounts } = await supabase
      .from("blog_post_view_counts")
      .select("post_id, view_count_unique, view_count_total")
      .in(
        "post_id",
        list.map((p) => p.id)
      );

    type ViewRow = {
      post_id: string;
      view_count_unique: number;
      view_count_total: number;
    };
    const viewByPostId = new Map<string, { unique: number; total: number }>();
    (viewCounts ?? []).forEach((r: ViewRow) => {
      viewByPostId.set(r.post_id, {
        unique: r.view_count_unique ?? 0,
        total: r.view_count_total ?? 0,
      });
    });
    setPosts(
      list.map((p) => {
        const v = viewByPostId.get(p.id);
        return {
          ...p,
          view_count_unique: v?.unique ?? 0,
          view_count_total: v?.total ?? 0,
        };
      })
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const openCreate = () => {
    const id = crypto.randomUUID();
    setEditingId(null);
    setDraftPostId(id);
    setCoverImagePath(null);
    setMedia([]);
    form.setFieldsValue({
      title: "",
      excerpt: "",
      body: "",
      is_published: false,
    });
    setModalOpen(true);
  };

  const openEdit = (row: BlogPost) => {
    setEditingId(row.id);
    setDraftPostId(null);
    setCoverImagePath(row.cover_image_path ?? null);
    setMedia(normalizeBlogMedia(row.media));
    form.setFieldsValue({
      title: row.title,
      excerpt: row.excerpt ?? "",
      body: row.body ?? "",
      is_published: row.is_published,
    });
    setModalOpen(true);
  };

  const activePostId = editingId ?? draftPostId;

  const handleSubmit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    if (!activePostId) {
      message.error("Missing post id");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        title: values.title,
        excerpt: values.excerpt ?? "",
        body: values.body ?? "",
        is_published: values.is_published ?? false,
        cover_image_path: coverImagePath,
        media,
      };

      if (editingId) {
        const { error } = await supabase
          .from("blog_posts")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
        message.success("Post updated");
      } else {
        const { error } = await supabase.from("blog_posts").insert({
          id: activePostId,
          ...payload,
          created_by: user?.id ?? null,
        });

        if (error) throw error;
        message.success("Post created");
      }
      setModalOpen(false);
      fetchPosts();
      trackFeatureUsage("admin_blog_save");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = (row: BlogPost) => {
    Modal.confirm({
      title: "Delete post",
      content: `Delete "${row.title}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error } = await supabase
          .from("blog_posts")
          .delete()
          .eq("id", row.id);
        if (error) {
          message.error("Failed to delete: " + error.message);
          return;
        }
        void removeBlogPostMediaFolder(row.id);
        message.success("Post deleted");
        fetchPosts();
        trackFeatureUsage("admin_blog_delete");
      },
    });
  };

  const handleSetPublished = async (row: BlogPost, checked: boolean) => {
    const { error } = await supabase
      .from("blog_posts")
      .update({ is_published: checked })
      .eq("id", row.id);

    if (error) {
      message.error("Failed to update: " + error.message);
      return;
    }
    message.success(checked ? "Post is now published" : "Post unpublished");
    fetchPosts();
    trackFeatureUsage("admin_blog_set_published", { is_published: checked });
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      ellipsis: true,
      render: (t: string) => t || "—",
    },
    {
      title: "Excerpt",
      dataIndex: "excerpt",
      key: "excerpt",
      width: 200,
      ellipsis: true,
      render: (s: string) =>
        (s || "—").slice(0, 60) + (s && s.length > 60 ? "…" : ""),
    },
    {
      title: "Published",
      dataIndex: "is_published",
      key: "is_published",
      width: 100,
      render: (_: boolean, row: BlogPost) => (
        <Switch
          checked={row.is_published}
          onChange={(c) => handleSetPublished(row, c)}
        />
      ),
    },
    {
      title: "Unique users",
      dataIndex: "view_count_unique",
      key: "view_count_unique",
      width: 80,
      render: (_: number | undefined, row: BlogPost) =>
        row.view_count_unique != null ? String(row.view_count_unique) : "—",
    },
    {
      title: "Total",
      dataIndex: "view_count_total",
      key: "view_count_total",
      width: 80,
      render: (_: number | undefined, row: BlogPost) =>
        row.view_count_total != null ? String(row.view_count_total) : "—",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 110,
      render: (d: string) => (d ? new Date(d).toLocaleDateString() : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_: unknown, row: BlogPost) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(row)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />
      <Title level={2}>
        <FileTextOutlined style={{ marginRight: 8 }} />
        Blog
      </Title>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add post
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchPosts}>
            Refresh
          </Button>
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={posts}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 860 }}
        />
      </Card>

      <Modal
        title={editingId ? "Edit post" : "Add post"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitLoading}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Post title" />
          </Form.Item>
          <Form.Item name="excerpt" label="Excerpt (short summary for lists)">
            <TextArea rows={2} placeholder="Brief summary" />
          </Form.Item>
          <Form.Item
            name="body"
            label="Body"
            rules={[{ required: true, message: "Required" }]}
          >
            <TextArea rows={6} placeholder="Full post content" />
          </Form.Item>
          <Form.Item
            name="is_published"
            label="Published"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>

        <Divider style={{ margin: "8px 0 16px" }} />
        {activePostId && (
          <BlogMediaEditor
            postId={activePostId}
            coverImagePath={coverImagePath}
            media={media}
            onCoverChange={setCoverImagePath}
            onMediaChange={setMedia}
          />
        )}
      </Modal>
    </div>
  );
};

export default BlogAdmin;
